import React from "react";
import { Box, Switch } from "@mui/joy";
import type { ZekayConfig } from "../../../api/config/Config.typed";
import useAppSettings from "../../../contexts/app/appSettings";
import { useAlert } from "../../../contexts/app/notificationToast";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import SettingsSectionCard from "../../shared/settingsSection/SettingsSectionCard";
import SettingsSectionEditToolbar from "../../shared/settingsSection/SettingsSectionEditToolbar";

type ExternalDraft = {
  customersSync: boolean;
  server: string;
  email: string;
  password: string;
};

function buildDraft(cfg: ZekayConfig): ExternalDraft {
  const z = cfg.ZuuritConfig || { Server: "", Email: "", Password: "" };
  return {
    customersSync: cfg.CustomersSync,
    server: z.Server || "",
    email: z.Email || "",
    password: z.Password || "",
  };
}

type Props = {
  config: ZekayConfig;
  canEditBackend: boolean;
};

export default function CentreExternalDataSection({ config, canEditBackend }: Props) {
  const { saveConfig } = useAppSettings();
  const { showAlert } = useAlert();

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<ExternalDraft | null>(null);

  const startEdit = () => {
    setDraft(buildDraft(config));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const save = React.useCallback(async () => {
    if (draft === null || saving) return;
    setSaving(true);
    try {
      const next: ZekayConfig = {
        ...config,
        CustomersSync: draft.customersSync,
        ZuuritConfig: {
          Server: draft.server,
          Email: draft.email,
          Password: draft.password,
        },
      };

      await saveConfig(next);
      showAlert("Enregistré", "success");
      cancelEdit();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Échec de l'enregistrement";
      showAlert(msg, "danger");
    } finally {
      setSaving(false);
    }
  }, [config, draft, saveConfig, showAlert, saving]);

  const d = editing && draft !== null ? draft : buildDraft(config);
  const fieldsEnabled = editing && canEditBackend;
  const zuuritFieldsEnabled = d.customersSync && fieldsEnabled;
  const showZuuritBlock = editing && d.customersSync;

  return (
    <SettingsSectionCard
      title="Données externes"
      titleEnd={
        <SettingsSectionEditToolbar
          canEditBackend={canEditBackend}
          isEditing={editing}
          saving={saving}
          onEdit={startEdit}
          onCancel={cancelEdit}
          onSave={() => void save()}
        />
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: showZuuritBlock ? 2 : 0 }}>
        <DarkTypography
          component="div"
          level="body-md"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          Synchronisation des clients
          <Switch
            checked={d.customersSync}
            onChange={(e) => {
              if (!fieldsEnabled) return;
              setDraft((prev) =>
                prev !== null ? { ...prev, customersSync: e.target.checked } : prev,
              );
            }}
            disabled={!fieldsEnabled}
          />
        </DarkTypography>
      </Box>

      {showZuuritBlock ? (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Box
            sx={{
              maxWidth: { xs: "95vw", sm: "80vw" },
              display: "grid",
              gridTemplateColumns: "200px 1fr",
              gap: 2,
              alignItems: "center",
            }}
          >
            <DarkTypography level="body-md" colorVariant="primary">
              Serveur
            </DarkTypography>
            <DarkInput
              placeholder="https://api.zuurit.com"
              value={d.server}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) => (prev !== null ? { ...prev, server: e.target.value } : prev))
              }
              disabled={!zuuritFieldsEnabled}
            />

            <DarkTypography level="body-md" colorVariant="primary">
              Email
            </DarkTypography>
            <DarkInput
              placeholder="email"
              value={d.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) => (prev !== null ? { ...prev, email: e.target.value } : prev))
              }
              disabled={!zuuritFieldsEnabled}
            />

            <DarkTypography level="body-md" colorVariant="primary">
              Mot de passe
            </DarkTypography>
            <DarkInput
              type="password"
              value={d.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) => (prev !== null ? { ...prev, password: e.target.value } : prev))
              }
              placeholder="Non affiché ; saisir pour modifier"
              sx={{ minWidth: 300 }}
              disabled={!zuuritFieldsEnabled}
            />
          </Box>
        </Box>
      ) : null}
    </SettingsSectionCard>
  );
}
